import { createSelector } from "@reduxjs/toolkit"
import { UnreachableCaseError } from "../../lib/lang"
import { RootState } from "../store/store"
import { UserType } from "../login/loginTypes"
import { selectServiceCenterId, selectUserCompanyId, selectUserGroupId, selectUserId, selectUsername, selectUserType } from "../login/loginSelectors"
import { selectLoginUser } from "../users/userSelectors"

export function selectActiveContext({ context }: RootState) {
    return context.active
}

export const selectEffectiveUserType = createSelector(selectActiveContext, selectUserType, (context, userType) => {
    if (!context) return userType

    switch (context.type) {
        case "supplier":
            return UserType.SUPPLIER
        case "merchant":
            return UserType.MERCHANT
        case "tenant":
            return UserType.OFFICE_MANAGER
        case "company":
            return UserType.COMPANY
        case "user":
            return UserType.USER
        case "serviceCenter":
            return UserType.SERVICE_CENTER
        default:
            throw new UnreachableCaseError(context)
    }
})

export const selectEffectiveUserId = createSelector(
    selectActiveContext,
    selectUserId,
    (context, userId) => {
        if (!context) return userId
        if (context.type !== "user") return userId
        return context.user.id
})

export const selectEffectiveUsername = createSelector(
    selectActiveContext,
    selectUsername,
    (context, username) => {
        if (!context) return username
        if (context.type !== "user") return username
        return context.user.username
    }
)

export const selectEffectiveDisplayName = createSelector(
    selectActiveContext,
    selectLoginUser,
    (context, user) => {
        if (!context) return user?.displayName
        if (context.type !== "user") return user?.displayName
        return context.user.displayName
    }
)

export const selectEffectiveCompanyId = createSelector(
    selectActiveContext,
    selectUserCompanyId,
    (context, companyId) => {
        if (!context) return companyId

        switch (context.type) {
            case "supplier":
                return context.supplier.id
            case "merchant":
                return context.merchant.id
            case "tenant":
                return context.tenant.companyId
            case "company":
                return context.company.id
            case "user":
                return context.user.company.id
            case "serviceCenter":
                return undefined
            default:
                throw new UnreachableCaseError(context)
        }
    },
)

export const selectEffectiveUserGroup = createSelector(
    selectActiveContext,
    selectUserGroupId,
    (context, userGroupId) => {
        if (!context) return userGroupId

        switch (context.type) {
            case "user":
                return context.user.userGroup.id
            case "tenant":
                return context.tenant.userGroupId
            case "supplier":
            case "merchant":
            case "serviceCenter":
            case "company":
                return undefined
            default:
                throw new UnreachableCaseError(context)
        }
    },
)

export const selectEffectiveServiceCenterId = createSelector(
    selectActiveContext,
    selectServiceCenterId,
    (context, serviceCenterId) => {
        if (!context) return serviceCenterId

        switch (context.type) {
            case "serviceCenter":
                return context.serviceCenter.id
            case "supplier":
            case "merchant":
            case "company":
            case "tenant":
            case "user":
                return undefined
            default:
                throw new UnreachableCaseError(context)
        }
    }
)
